import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./auth";
import { useApi } from "./index";
import type { AuthUser } from "./types";

/**
 * Re-syncs the cached session user from `GET /api/auth/me` on mount and whenever the tab regains focus, so role and permission changes
 * made in the Platform Console apply to signed-in staff without a new sign-in. A 401 (expired/revoked token) clears the session.
 */
export function useSessionRefresh(intervalMs = 5 * 60_000): void {
  const api = useApi();
  const { isAuthenticated, token, updateUser, clear } = useAuth();
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const me = await api.get<AuthUser>("/api/auth/me");
        if (!cancelled) updateUser(me);
      } catch (e) {
        const status = (e as { status?: number } | undefined)?.status;
        if (!cancelled && status === 401) clear();
      }
    };
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    const timer = window.setInterval(refresh, intervalMs);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      window.clearInterval(timer);
    };
    // `api` and `token` identify the session; a new token restarts the loop.
  }, [api, isAuthenticated, token, updateUser, clear, intervalMs]);
}

/**
 * Platform permissions are dotted capability keys (`platform.workflows.publish`). Roles map to permissions in the roles table;
 * the API resolves the effective set at sign-in and puts it on the session user. `*` grants everything; a trailing `.*`
 * grants a whole area (`platform.workflows.*`).
 */
export function permissionMatches(granted: readonly string[] | null | undefined, required: string): boolean {
  if (!granted?.length) return false;
  for (const g of granted) {
    if (g === "*" || g === required) return true;
    if (g.endsWith(".*") && required.startsWith(g.slice(0, -1))) return true;
  }
  return false;
}

export interface PermissionApi {
  /** True when the signed-in user holds the permission. Platform administrators always pass. */
  can: (permission: string) => boolean;
  /** True when the user holds at least one of the permissions. */
  canAny: (...permissions: string[]) => boolean;
  /** Effective permissions of the session (may be `["*"]`). */
  permissions: readonly string[];
  isPlatformAdmin: boolean;
}

/**
 * Permission checks for the signed-in user. Sessions issued before the permission model existed carry no `permissions`;
 * for those the legacy `Admin` staff role is treated as `*` so existing administrators are never locked out.
 */
export function usePermission(): PermissionApi {
  const { user } = useAuth();
  const permissions = useMemo<readonly string[]>(() => {
    if (!user) return [];
    if (user.permissions?.length) return user.permissions;
    return user.roles.includes("Admin") ? ["*"] : [];
  }, [user]);
  const can = useCallback((p: string) => permissionMatches(permissions, p), [permissions]);
  const canAny = useCallback((...ps: string[]) => ps.some((p) => permissionMatches(permissions, p)), [permissions]);
  return { can, canAny, permissions, isPlatformAdmin: permissions.includes("*") };
}

export interface FeatureFlagState {
  key: string;
  enabled: boolean;
  /** Present when the flag is targeted (roles, users, percentage) rather than a plain switch. */
  reason?: string | null;
}

/**
 * Feature flags evaluated by the API for the signed-in user (`GET /api/platform/flags/evaluate`).
 * `useFlag("portal.dynamicIntake.lq")` returns `false` until flags load or when the flag does not exist.
 */
export function useFlags(enabled = true) {
  const api = useApi();
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["platform", "flags", "evaluate"],
    queryFn: () => api.get<FeatureFlagState[]>("/api/platform/flags/evaluate"),
    enabled: enabled && isAuthenticated,
    staleTime: 60_000,
  });
}

export function useFlag(key: string, fallback = false): boolean {
  const flags = useFlags();
  const flag = flags.data?.find((f) => f.key === key);
  return flag ? flag.enabled : fallback;
}
